/**
 * Paramètres fiscaux DATÉS issus du rapport comparatif CGI 2022 / 2026 reçu du dirigeant.
 *
 * Règles (18/10/2026) :
 * - Règle du dirigeant : à chaque conflit entre sources, on garde la plus RÉCENTE (date de publication ou de
 *   consolidation). Une ligne est active si elle est `etabli` (texte officiel : CGI 2025 publié par la DGI) ou
 *   `retenu_source_recente` (valeur retenue par cette règle faute de texte officiel plus récent, à confirmer
 *   au JO). Lignes actives : IS minier 35 %, retenue sur loyers 5 % (art. 178 bis), plus-values de non-résidents.
 * - Les lignes qui ne viennent que du rapport 2026 (source secondaire, LFR 2026 « rapportée ») et que le CGI 2025
 *   contredit ou ne confirme pas restent `actif: false`.
 * - Une ligne n'est jamais modifiée une fois publiée : un changement de loi = NOUVELLE ligne datée.
 * - Statut « a_valider_expert » : à confirmer par l'expert-comptable (puis `actif: true` dans une nouvelle
 *   version de la table ou un patch entreprise) ; « a_confirmer_jo » : texte officiel à contrôler.
 * - Cette table est le pendant fiscal (hors paie) de `ParametresPaieDates.ts` et de `BaremeTva.ts`.
 *
 * Voir `docs/decisions/DECISIONS_DIRIGEANT.md` (section « Rapport fiscal CGI 2026 reçu »).
 */

export type IdParametreFiscal =
  | 'retenue_loyers_178bis'
  | 'allocation_vehicule_plafond_mensuel'
  | 'plus_value_non_residents_taux'
  | 'is_activites_minieres_taux'
  | 'facture_normalisee_electronique_exigee';

export type StatutParametreFiscal =
  | 'a_valider_expert'
  | 'a_confirmer_jo'
  | 'etabli'
  | 'retenu_source_recente';

export interface ParametreFiscalDate {
  readonly id: IdParametreFiscal;
  /** Taux en fraction (0,05 = 5 %), montant en FCFA, ou 1 / 0 pour une exigence (oui / non). */
  readonly valeur: number;
  /** Date d'effet AAAA-MM-JJ (incluse). */
  readonly date_effet: string;
  /** false = n'a AUCUN effet sur les calculs. */
  readonly actif: boolean;
  readonly statut: StatutParametreFiscal;
  /** Article du CGI visé. */
  readonly article: string;
  readonly source: string;
}

export const PARAMETRES_FISCAUX_DATES: readonly ParametreFiscalDate[] = [
  {
    id: 'retenue_loyers_178bis',
    valeur: 0.05,
    date_effet: '2017-01-01',
    actif: true,
    statut: 'etabli',
    article: 'CGI art. 178 bis',
    source: 'CGI 2025 publié par la DGI, art. 178 bis : précompte de 5 % du montant brut hors taxes des loyers encaissés, opéré par l\'agence immobilière, le gérant de biens ou la SCI ; locataire individuel dispensé ; non applicable lorsque le bailleur est une personne morale à l\'IS. Page DGI « modalités de recouvrement » : « Précompte de 5 % (LF 2017) ». Remplace le 10 % historique du code, qui est le taux du précompte du Trésor sur ses fournisseurs (art. 178) et non celui de l\'art. 178 bis.',
  },
  {
    id: 'retenue_loyers_178bis',
    valeur: 0.1,
    date_effet: '2026-01-01',
    actif: false,
    statut: 'a_confirmer_jo',
    article: 'CGI art. 178 bis',
    source: 'Rapport fiscal : taux de 10 % de la loi de finances initiale 2026 (loi 041/2025). NON retenu le 18/10/2026 : texte de la loi non disponible, source secondaire, et le CGI 2025 (plus récent texte lu) dit 5 %.',
  },
  {
    id: 'retenue_loyers_178bis',
    valeur: 0.05,
    date_effet: '2026-07-17',
    actif: false,
    statut: 'a_valider_expert',
    article: 'CGI art. 178 bis, al. 2 nouveau',
    source: 'Rapport fiscal : taux de 5 % de la loi de finances rectificative 2026 (JO n° 123 Bis du 17/07/2026, source secondaire). Même valeur que la ligne établie de 2017 : conservée pour l\'historique, sans effet propre.',
  },
  {
    id: 'allocation_vehicule_plafond_mensuel',
    valeur: 100_000,
    date_effet: '2026-01-01',
    actif: false,
    statut: 'a_confirmer_jo',
    article: 'CGI art. 91 bis, al. 5 nouveau',
    source: 'Rapport fiscal : plafond mensuel de l\'allocation ou indemnité de véhicule. Identique à PLAFOND_VEHICULE_EXONERE déjà appliqué en paie : aucun changement ; ligne conservée pour la traçabilité.',
  },
  {
    id: 'plus_value_non_residents_taux',
    valeur: 0.2,
    date_effet: '1970-01-01',
    actif: true,
    statut: 'retenu_source_recente',
    article: 'CGI 2025 art. 126 (plus-values des particuliers) ; article propre aux non-résidents à confirmer',
    source: 'CGI 2025 : 20 % du montant net des plus-values (art. 126) ; rapport fiscal : « ancien taux » de 20 %. Valable jusqu\'au 31/12/2025, date d\'effet du taux plus récent ci-dessous.',
  },
  {
    id: 'plus_value_non_residents_taux',
    valeur: 0.25,
    date_effet: '2026-01-01',
    actif: true,
    statut: 'retenu_source_recente',
    article: 'article à confirmer',
    source: 'Rapport fiscal 2026 : hausse de 20 % à 25 % rapportée (loi de finances 2026), source plus récente que le CGI 2025 qui ne connaît que 20 %. Retenu le 18/10/2026 par la règle de la source la plus récente ; texte officiel (loi 041/2025, LFR 2026) non disponible, champ « certains cas » à confirmer. Aucune plus-value de non-résident n\'est calculée par l\'ERP : la ligne n\'a aucun effet sur un calcul.',
  },
  {
    id: 'is_activites_minieres_taux',
    valeur: 0.35,
    date_effet: '2022-01-01',
    actif: true,
    statut: 'etabli',
    article: 'CGI art. 16',
    source: 'DGI : « Ce taux est porté à 35 % pour les entreprises du secteur pétrolier et minier » (CGI 2025 publié par la DGI, art. 16, mention L.F.2022 ; page https://dgi.ga/imposition-des-personnes-morales/impot-sur-les-societes/). Ferme le point « IS des mines » ; appliqué par le régime `minier` (IS_MINES). Date d\'effet = exercice 2022 de la loi de finances 2022 (la date exacte de publication n\'est pas lue dans le texte).',
  },
  {
    id: 'is_activites_minieres_taux',
    valeur: 0.35,
    date_effet: '2026-01-01',
    actif: false,
    statut: 'a_valider_expert',
    article: 'CGI (IS, régime sectoriel)',
    source: 'Rapport fiscal : la DGI indique 35 % pour les activités pétrolières ET minières. Écart FERMÉ le 18/10/2026 par la ligne `etabli` de 2022 ci-dessus ; cette ligne inactive est conservée pour l\'historique.',
  },
  {
    id: 'facture_normalisee_electronique_exigee',
    valeur: 1,
    date_effet: '2026-01-01',
    actif: false,
    statut: 'a_confirmer_jo',
    article: 'CGI art. 11 (charges) et art. 223 (déduction de TVA)',
    source: 'Rapport fiscal et réponses légales : la TVA n\'est déductible que sur facture électronique normalisée (période transitoire de 6 mois pour certains justificatifs de droits de douane). Textes d\'application et date à confirmer ; l\'ERP n\'impose rien aujourd\'hui.',
  },
];

export class ParametreFiscalInvalideError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ParametreFiscalInvalideError';
  }
}

const FORMAT_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Valide une table : dates ISO réelles, valeurs finies et positives, pas de doublon id+date. */
export function validerParametresFiscaux(table: readonly ParametreFiscalDate[]): void {
  const vus = new Set<string>();
  for (const l of table) {
    if (!FORMAT_DATE.test(l.date_effet) || Number.isNaN(Date.parse(l.date_effet))) {
      throw new ParametreFiscalInvalideError(`Date d'effet invalide (${l.id}) : ${l.date_effet} (AAAA-MM-JJ attendu).`);
    }
    if (!Number.isFinite(l.valeur) || l.valeur < 0) {
      throw new ParametreFiscalInvalideError(`Valeur invalide (${l.id}) : ${l.valeur}.`);
    }
    const cle = `${l.id}@${l.date_effet}`;
    if (vus.has(cle)) throw new ParametreFiscalInvalideError(`Deux lignes ${l.id} pour la même date ${l.date_effet}.`);
    vus.add(cle);
  }
}

export interface OptionsParametreFiscal {
  /** true = lit aussi les lignes inactives (consultation, jamais pour un calcul). */
  readonly inclureInactifs?: boolean;
}

/**
 * Ligne applicable à une date : la plus récente déjà en vigueur parmi les lignes ACTIVES
 * (ou toutes avec `inclureInactifs`). `undefined` si aucune : l'appelant garde alors sa valeur actuelle.
 */
export function parametreFiscalAu(
  id: IdParametreFiscal,
  date: string,
  table: readonly ParametreFiscalDate[] = PARAMETRES_FISCAUX_DATES,
  options: OptionsParametreFiscal = {},
): ParametreFiscalDate | undefined {
  const jour = date.slice(0, 10);
  let retenu: ParametreFiscalDate | undefined;
  for (const l of table) {
    if (l.id !== id || l.date_effet > jour) continue;
    if (!l.actif && !options.inclureInactifs) continue;
    if (!retenu || l.date_effet > retenu.date_effet) retenu = l;
  }
  return retenu;
}
